// @ts-check
const MAX_BYTES = 250 * 1024 * 1024;
const HARD_PAGE_LIMIT = 1000;
const HARD_MEMORY_FRACTION = 0.95; // reject if estimate exceeds this fraction of heap limit
const SOFT_MEMORY_FRACTION = 0.75; // warn if estimate exceeds this fraction of heap limit

// pdf-lib keeps a parsed representation plus embedded copies; this is a
// conservative per-page overhead estimate on top of the raw PDF bytes.
const BYTES_PER_PAGE_OVERHEAD = 2_500_000;

const form = /** @type {HTMLFormElement} */ (document.getElementById('form'));
const dropZone = /** @type {HTMLElement} */ (document.getElementById('drop-zone'));
const fileInput = /** @type {HTMLInputElement} */ (document.getElementById('file'));
const sigSelect = /** @type {HTMLSelectElement} */ (document.getElementById('signature'));
const flipSelect = /** @type {HTMLSelectElement} */ (document.getElementById('flip'));
const statusEl = /** @type {HTMLParagraphElement} */ (document.getElementById('status'));
const progressEl = /** @type {HTMLProgressElement} */ (document.getElementById('progress'));
const downloadEl = /** @type {HTMLAnchorElement} */ (document.getElementById('download'));
const button = /** @type {HTMLButtonElement} */ (document.getElementById('go'));
const hintFlip = /** @type {HTMLElement} */ (document.getElementById('hint-flip'));

const summaryEl = /** @type {HTMLElement} */ (document.getElementById('summary'));
const summarySource = /** @type {HTMLElement} */ (document.getElementById('summary-source'));
const summaryBlanks = /** @type {HTMLElement} */ (document.getElementById('summary-blanks'));
const summarySheets = /** @type {HTMLElement} */ (document.getElementById('summary-sheets'));
const summarySigs = /** @type {HTMLElement} */ (document.getElementById('summary-sigs'));
const summaryMemory = /** @type {HTMLElement} */ (document.getElementById('summary-memory'));

const previewEl = /** @type {HTMLElement} */ (document.getElementById('preview'));
const previewCanvas = /** @type {HTMLCanvasElement} */ (document.getElementById('preview-canvas'));
const previewCtx = /** @type {CanvasRenderingContext2D} */ (previewCanvas.getContext('2d'));

let worker = null;
let currentFile = null;
let currentPageCount = 0;
let currentPdfBytes = null;
let currentDownloadUrl = null;

restoreSettings();

fileInput.addEventListener('change', () => {
  const file = fileInput.files[0];
  if (file) handleFile(file);
});

sigSelect.addEventListener('change', () => {
  saveSettings();
  if (currentPageCount > 0) updateSummary();
});

flipSelect.addEventListener('change', () => {
  saveSettings();
  updateFlipHint();
  if (currentPageCount > 0) drawPreview();
});

updateFlipHint();

// Drag-and-drop
['dragenter', 'dragover', 'dragleave', 'drop'].forEach((name) => {
  dropZone.addEventListener(name, preventDefaults, false);
  document.body.addEventListener(name, preventDefaults, false);
});

['dragenter', 'dragover'].forEach((name) => {
  dropZone.addEventListener(name, () => dropZone.classList.add('drop-active'), false);
});

['dragleave', 'drop'].forEach((name) => {
  dropZone.addEventListener(name, () => dropZone.classList.remove('drop-active'), false);
});

dropZone.addEventListener('drop', (e) => {
  const dt = e.dataTransfer;
  const files = dt.files;
  if (files.length) {
    fileInput.files = files;
    handleFile(files[0]);
  }
}, false);

form.addEventListener('submit', async (e) => {
  e.preventDefault();
  const file = currentFile || fileInput.files[0];
  if (!file) {
    show(statusEl, 'Choose a PDF first.');
    return;
  }

  if (file.size > MAX_BYTES) {
    show(statusEl, `PDF too large (${formatBytes(file.size)}). Maximum is ${formatBytes(MAX_BYTES)}.`);
    return;
  }

  if (currentPageCount === 0) {
    show(statusEl, 'Could not read the page count. Try another file.');
    return;
  }

  if (currentPageCount > HARD_PAGE_LIMIT) {
    show(statusEl, `This PDF has ${currentPageCount} pages. The maximum supported is ${HARD_PAGE_LIMIT}.`);
    return;
  }

  const sigSize = parseInt(sigSelect.value, 10);
  const padded = Math.ceil(currentPageCount / (sigSize || 4)) * (sigSize || 4);
  const memory = estimateMemoryNeed(file.size, padded);
  const heap = getHeapInfo();

  if (memory.hardRejected) {
    show(statusEl,
      `This PDF would need about ${formatBytes(memory.estimate)} of browser memory, ` +
      `but only ${formatBytes(heap.available)} is available. Try a shorter document or a different browser.`
    );
    return;
  }

  if (memory.warn) {
    const proceed = confirm(
      `This PDF may need about ${formatBytes(memory.estimate)} of browser memory ` +
      `(${heap.available ? formatBytes(heap.available) + ' currently available' : 'memory information unavailable'}). ` +
      `Large documents can crash the browser tab. Proceed anyway?`
    );
    if (!proceed) return;
  }

  setBusy(true);
  hide(downloadEl);
  revokeCurrentDownload();
  show(statusEl, 'Reading PDF…');
  show(progressEl);
  progressEl.value = 0;
  previewEl.hidden = true;

  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const out = await runWorkerImpose(bytes, {
      signatureSize: sigSize,
      flip: flipSelect.value,
    });

    const blob = new Blob([out], { type: 'application/pdf' });
    currentDownloadUrl = URL.createObjectURL(blob);
    downloadEl.href = currentDownloadUrl;
    downloadEl.download = `${sanitizeFileName(file.name)}-booklet.pdf`;
    show(downloadEl);
    show(statusEl, 'Done.');
    hide(progressEl);
    drawPreviewFromOrder();
    downloadEl.focus();
  } catch (err) {
    hide(progressEl);
    show(statusEl, humanError(err));
    console.error(err);
  } finally {
    setBusy(false);
  }
});

async function handleFile(file) {
  currentFile = file;
  currentPageCount = 0;
  currentPdfBytes = null;
  hide(downloadEl);
  revokeCurrentDownload();

  if (file.size > MAX_BYTES) {
    show(statusEl, `PDF too large (${formatBytes(file.size)}). Maximum is ${formatBytes(MAX_BYTES)}.`);
    summaryEl.hidden = true;
    previewEl.hidden = true;
    return;
  }

  show(statusEl, 'Inspecting PDF…');
  try {
    currentPdfBytes = new Uint8Array(await file.arrayBuffer());
    const info = await runWorkerAnalyze(currentPdfBytes);
    currentPageCount = info.pageCount;
    updateSummary();
    drawPreview();
    hide(statusEl);
  } catch (err) {
    currentPdfBytes = null;
    show(statusEl, humanError(err));
    console.error(err);
    summaryEl.hidden = true;
    previewEl.hidden = true;
  }
}

function updateSummary() {
  if (!currentFile || currentPageCount === 0) {
    summaryEl.hidden = true;
    return;
  }

  const sigSize = parseInt(sigSelect.value, 10);
  const padded = Math.ceil(currentPageCount / (sigSize || 4)) * (sigSize || 4);
  const sheets = padded / 4;
  const sigCount = sigSize > 0 ? padded / sigSize : 1;
  const blanks = padded - currentPageCount;
  const memory = estimateMemoryNeed(currentFile.size, padded);

  summarySource.textContent = String(currentPageCount);
  summaryBlanks.textContent = String(blanks);
  summarySheets.textContent = String(sheets);
  summarySigs.textContent = String(sigCount);

  const heap = getHeapInfo();
  if (heap.available) {
    summaryMemory.textContent =
      `Estimated ~${formatBytes(memory.estimate)} needed, ${formatBytes(heap.available)} available.`;
  } else {
    summaryMemory.textContent = `Estimated ~${formatBytes(memory.estimate)} needed.`;
  }

  if (memory.hardRejected) {
    summaryMemory.textContent += ' ⚠️ Too large for this browser.';
  } else if (memory.warn) {
    summaryMemory.textContent += ' ⚠️ May run out of memory.';
  }

  summaryEl.hidden = false;
}

function estimateMemoryNeed(fileSize, paddedPageCount) {
  // Conservative heuristic:
  // - keep the raw input once loaded,
  // - pdf-lib parses and keeps a structural representation (~1× file size),
  // - embedding copies page content into the output (~1× file size),
  // - each page adds pdf-lib object overhead.
  const estimate = fileSize * 3 + paddedPageCount * BYTES_PER_PAGE_OVERHEAD;
  const heap = getHeapInfo();

  if (!heap.limit) {
    // No memory API: fall back to the old conservative page/size checks.
    return {
      estimate,
      warn: paddedPageCount > 500 || fileSize > 100 * 1024 * 1024,
      hardRejected: paddedPageCount > 1000 || fileSize > 250 * 1024 * 1024,
    };
  }

  const available = heap.available || heap.limit;
  return {
    estimate,
    warn: estimate > available * SOFT_MEMORY_FRACTION,
    hardRejected: estimate > available * HARD_MEMORY_FRACTION,
  };
}

function getHeapInfo() {
  const perf = /** @type {any} */ (performance);
  const mem = perf.memory;
  if (!mem || typeof mem.jsHeapSizeLimit !== 'number') {
    return { limit: 0, used: 0, available: 0 };
  }
  return {
    limit: mem.jsHeapSizeLimit,
    used: mem.usedJSHeapSize || 0,
    available: mem.jsHeapSizeLimit - (mem.usedJSHeapSize || 0),
  };
}

function drawPreview() {
  if (!currentFile || currentPageCount === 0) {
    previewEl.hidden = true;
    return;
  }
  drawPreviewFromOrder();
  previewEl.hidden = false;
}

function drawPreviewFromOrder() {
  // Schematic preview of the first output sheet (front face).
  // We use the same ordering math as the imposer but draw page numbers instead
  // of rendering actual PDF content.
  import('./imposition.js').then(({ padToSignature, computeBookletOrder }) => {
    const signatureSize = parseInt(sigSelect.value, 10);
    const padded = padToSignature(currentPageCount, signatureSize);
    const order = computeBookletOrder(padded, signatureSize);
    if (!order.length) return;

    const firstSide = order[0];
    const flip = flipSelect.value;

    const W = previewCanvas.width;
    const H = previewCanvas.height;
    const half = W / 2;

    previewCtx.clearRect(0, 0, W, H);
    previewCtx.fillStyle = '#ffffff';
    previewCtx.fillRect(0, 0, W, H);

    previewCtx.strokeStyle = '#d4d4d4';
    previewCtx.lineWidth = 2;
    previewCtx.strokeRect(0, 0, W, H);
    previewCtx.beginPath();
    previewCtx.moveTo(half, 0);
    previewCtx.lineTo(half, H);
    previewCtx.stroke();

    drawHalfPreview(half, H, 0, firstSide.left, 'Front left');
    drawHalfPreview(half, H, half, firstSide.right, 'Front right');

    if (flip === 'short-edge') {
      previewCtx.fillStyle = '#666';
      previewCtx.font = '14px system-ui, sans-serif';
      previewCtx.textAlign = 'center';
      previewCtx.fillText('Short-edge flip: back side will be mirrored + rotated 180°', W / 2, H - 12);
    }
  });
}

function drawHalfPreview(canvasW, canvasH, xOffset, sourceIdx, label) {
  const isBlank = sourceIdx >= currentPageCount;
  const text = isBlank ? 'blank' : `page ${sourceIdx + 1}`;

  previewCtx.fillStyle = isBlank ? '#f5f5f5' : '#e8f0ff';
  previewCtx.fillRect(xOffset + 4, 4, canvasW / 2 - 8, canvasH - 8);

  previewCtx.fillStyle = '#1a1a1a';
  previewCtx.font = 'bold 18px system-ui, sans-serif';
  previewCtx.textAlign = 'center';
  previewCtx.textBaseline = 'middle';
  previewCtx.fillText(text, xOffset + canvasW / 4, canvasH / 2 - 10);

  previewCtx.fillStyle = '#666';
  previewCtx.font = '12px system-ui, sans-serif';
  previewCtx.fillText(label, xOffset + canvasW / 4, canvasH / 2 + 14);
}

function runWorkerAnalyze(bytes) {
  return new Promise((resolve, reject) => {
    const w = getWorker();
    // Do not transfer the buffer: the main thread keeps the bytes for later imposition.
    w.postMessage({ type: 'analyze', pdfBytes: bytes });

    function onMessage(e) {
      if (e.data.type === 'analyzed') {
        cleanup();
        resolve(e.data);
      } else if (e.data.type === 'error') {
        cleanup();
        reject(new Error(e.data.message));
      }
    }

    function cleanup() {
      w.removeEventListener('message', onMessage);
    }

    w.addEventListener('message', onMessage);
  });
}

function runWorkerImpose(bytes, options) {
  return new Promise((resolve, reject) => {
    const w = getWorker();
    w.postMessage({ type: 'impose', pdfBytes: bytes, options }, [bytes.buffer]);

    function onMessage(e) {
      if (e.data.type === 'progress') {
        progressEl.value = e.data.percent;
        statusEl.textContent = stageLabel(e.data.stage);
      } else if (e.data.type === 'done') {
        cleanup();
        resolve(e.data.pdfBytes);
      } else if (e.data.type === 'error') {
        cleanup();
        reject(new Error(e.data.message));
      }
    }

    function cleanup() {
      w.removeEventListener('message', onMessage);
    }

    w.addEventListener('message', onMessage);
  });
}

function getWorker() {
  if (!worker) {
    // new URL(...) is the pattern bundlers (Vite, webpack) use to discover
    // and bundle module workers. It also works for plain static hosting.
    worker = new Worker(new URL('./imposition.worker.js', import.meta.url), { type: 'module' });
  }
  return worker;
}

function stageLabel(stage) {
  switch (stage) {
    case 'load': return 'Reading PDF…';
    case 'embed': return 'Embedding pages…';
    case 'draw': return 'Imposing pages…';
    case 'save': return 'Saving PDF…';
    case 'done': return 'Done.';
    default: return 'Working…';
  }
}

function humanError(err) {
  const msg = String(err && err.message || err).toLowerCase();
  if (msg.includes('encrypt') || msg.includes('password')) {
    return 'This PDF is password-protected. Remove the password first, then try again.';
  }
  if (msg.includes('no pdf header') || msg.includes('not a valid pdf') || msg.includes('failed to parse')) {
    return 'This file does not look like a valid PDF. It may be corrupted or in another format.';
  }
  if (msg.includes('missing') && msg.includes('pdf')) {
    return 'The PDF appears to be truncated or damaged.';
  }
  if (msg.includes('memory') || msg.includes('out of memory') || msg.includes('allocation')) {
    return 'The PDF is too large for this browser to process. Try a shorter document or a different browser.';
  }
  return `Something went wrong: ${err.message || err}`;
}

function sanitizeFileName(name) {
  const base = name.replace(/\.pdf$/i, '');
  const safe = base
    .replace(/[^a-zA-Z0-9\-_ ]+/g, '_')
    .replace(/^[.\s]+|[.\s]+$/g, '')
    .slice(0, 100)
    || 'booklet';
  return safe;
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function preventDefaults(e) {
  e.preventDefault();
  e.stopPropagation();
}

function updateFlipHint() {
  const flip = flipSelect.value;
  hintFlip.textContent = flip === 'long-edge' ? 'flip on long edge' : 'flip on short edge';
}

function saveSettings() {
  try {
    localStorage.setItem('booklet-signature', sigSelect.value);
    localStorage.setItem('booklet-flip', flipSelect.value);
  } catch {
    // localStorage may be disabled; ignore.
  }
}

function restoreSettings() {
  try {
    const sig = localStorage.getItem('booklet-signature');
    const flip = localStorage.getItem('booklet-flip');
    if (sig) sigSelect.value = sig;
    if (flip) flipSelect.value = flip;
  } catch {
    // ignore
  }
}

function revokeCurrentDownload() {
  if (currentDownloadUrl) {
    URL.revokeObjectURL(currentDownloadUrl);
    currentDownloadUrl = null;
    downloadEl.removeAttribute('href');
  }
}

function setBusy(busy) {
  button.disabled = busy;
  button.setAttribute('aria-busy', busy ? 'true' : 'false');
  button.textContent = busy ? 'Generating…' : 'Generate booklet';
}

function show(el, text) {
  if (text !== undefined) el.textContent = text;
  el.hidden = false;
}

function hide(el) {
  el.hidden = true;
}
